import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getDatabase, ref as dbRef, push } from 'firebase/database';
import { FileAttachment } from '../types/attachments';
import { formatDateForStorage } from './DateUtils';
import { recordActivity } from '../api/activityApi';

export interface OperatingCostAttachmentUploadResult {
    success: boolean;
    attachment?: FileAttachment;
    error?: string;
}

/**
 * Upload a file attachment to an operating cost
 */
export const uploadOperatingCostAttachment = async (
    buildingKey: string,
    costKey: string,
    file: File,
    uploadedBy: string
): Promise<OperatingCostAttachmentUploadResult> => {
    try {
        const db = getDatabase();
        const storage = getStorage();

        // Generate unique attachment key using Firebase push
        const attachmentsRef = dbRef(db, `operatingCosts/${buildingKey}/${costKey}/attachments`);
        const newAttachmentRef = push(attachmentsRef);
        const attachmentKey = newAttachmentRef.key!;

        // Storage path for operating cost attachments
        const storagePath = `operatingCosts/${buildingKey}/${costKey}/${attachmentKey}`;

        // Upload file to Storage
        const fileRef = storageRef(storage, storagePath);
        await uploadBytes(fileRef, file);
        recordActivity('document.uploaded', { buildingKey, targetKey: attachmentKey });

        // Get file extension
        const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';

        // Create file attachment (operating costs only support file attachments, not images)
        const fileAttachment: FileAttachment = {
            key: attachmentKey,
            fileName: file.name,
            fileType: fileExtension,
            mimeType: file.type,
            storagePath: storagePath,
            fileSize: file.size,
            uploadedBy: uploadedBy,
            uploadedAt: formatDateForStorage(new Date()),
            attachmentType: 'file'
        };

        return {
            success: true,
            attachment: fileAttachment
        };
    } catch (error) {
        console.error('Error uploading operating cost attachment:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Ett fel uppstod vid uppladdning'
        };
    }
};

/**
 * Get download URL for an attachment
 */
export const getAttachmentDownloadUrl = async (
    storagePath: string
): Promise<string> => {
    try {
        const storage = getStorage();
        const fileRef = storageRef(storage, storagePath);
        return await getDownloadURL(fileRef);
    } catch (error) {
        console.error('Error getting attachment download URL:', error);
        throw error;
    }
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

/**
 * Get icon name based on file type
 */
export const getFileIcon = (fileType: string): string => {
    const type = fileType.toLowerCase();
    if (['pdf'].includes(type)) return 'file-text';
    if (['doc', 'docx'].includes(type)) return 'file-text';
    if (['xls', 'xlsx'].includes(type)) return 'file-spreadsheet';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(type)) return 'image';
    return 'file';
};
